import { colors } from '../customTheme';
import clothing from '../images/sell-clothes-online/clothes.png';

const sellClothesOnline = `
~~~~light,${colors.white}
# Where and How to Sell Clothes Online

<div style="text-align: center; font-family: Inter; margin-bottom: 6px">
Last updated July 3, 2025
</div>
<div style="text-align: center; font-family: Inter">
Written by Alice
</div>
\n&nbsp;\n

![Picture showing clothing items ready for resale](${clothing})

If you're looking to clean out your closet or start a side hustle selling secondhand clothes, there's never been a better time. The resale market is booming, with the global secondhand apparel market expected to reach $84 billion by 2030. With dozens of resale platforms to choose from, knowing where to sell clothes online—and how much effort each one takes—can be overwhelming.

Whether you've got luxury designer items, trendy vintage pieces, or everyday basics, this guide compares seven of the most popular platforms: ThredUp, eBay, The RealReal, WhatNot, Poshmark, Mercari, and Depop. We'll break down what each site is known for, how much money you can make, the shipping process, and which platform is best suited for your selling goals.

~~~~light,${colors.white}
#### ThredUp: The "Set It and Forget It" Option

ThredUp is perfect if you want to sell your clothes with minimal effort. You simply request a Clean Out Kit, fill it with clothes, and ship it back. ThredUp's team handles photography, pricing, and shipping for you.

**Best for**: Busy people decluttering in bulk

**Effort level**: Very low

**Earnings**: 
- Premium brands (Lululemon, Patagonia, etc.): 60-80% of selling price
- Mid-tier brands (J.Crew, Banana Republic): 30-50% of selling price
- Fast fashion (H&M, Forever 21): 5-15% of selling price
- Items under $20 often net just $2-5

**Fees**: No upfront fees, but you pay for the Clean Out Kit ($14.99) and shipping

**Processing Time**: 2-4 weeks for initial processing, then items sell over 90 days

**Shipping**: You pay $14.99 for Clean Out Kit + shipping to ThredUp

**Buyer audience**: Eco-conscious and brand-savvy shoppers

**Seller profile**: Casual or one-time sellers who want zero hassle

**What They Accept**: Women's and kids' clothing, shoes, and accessories in good condition

**What They Reject**: Items with stains, holes, pilling, or strong odors (about 40% of items sent in)

This is a great choice if you don't have time to list items individually but are okay with lower returns and less control.

~~~~light,${colors.white}
#### eBay: The Global Resale Giant

eBay remains the most versatile marketplace for selling clothes online. It supports both auction and fixed-price listings, making it ideal for everything from vintage tees to high-end fashion.

**Best for**: Sellers with rare items or large, varied inventory

**Effort level**: Moderate to high—you manage listings, messages, and shipping

**Earnings**: 
- Final value fees: 12.9% of total sale amount
- PayPal fees: 2.9% + $0.30 per transaction
- Promoted listing fees: 1-15% additional (optional)
- Average seller earns $200-2,000+ monthly depending on inventory

**Listing Fees**: First 250 listings per month are free, then $0.35 each

**Shipping**: You handle shipping using eBay's discounted labels (saves 10-30% vs. retail)

**Buyer audience**: Bargain hunters, collectors, international shoppers (190+ countries)

**Seller profile**: Resellers, flippers, and anyone comfortable managing the full process

**Success Metrics**: 
- Items typically sell within 30-90 days
- Best selling categories: vintage, designer, and rare items
- International sales can increase earnings by 20-40%

eBay's reach is unmatched, but it works best if you're willing to put in the effort to optimize your listings and customer service.

~~~~light,${colors.white}
#### The RealReal: High-End Designer Consignment

Got luxury fashion items collecting dust? The RealReal specializes in authenticated designer goods. You send your items to them, and they handle everything from verification to pricing to shipping.

**Best for**: Selling authentic designer pieces

**Effort level**: Low—just ship your items

**Earnings**: 
- Commission rates: 20-30% of selling price
- Higher-tier sellers (50+ items): 30-40%
- Items under $200 often not worth it after fees
- Average item sells for $200-800

**Processing Time**: 2-3 weeks for authentication and listing

**Shipping**: You pay shipping to The RealReal (typically $15-25)

**Buyer audience**: Luxury shoppers looking for authenticated deals

**Seller profile**: Owners of high-end pieces who want a premium consignment experience

**What They Accept**: Authentic designer items from brands like Chanel, Gucci, Louis Vuitton, Hermès, etc.

**Authentication Process**: Items are verified by experts and photographed professionally

Just know that items under $200 may not earn much after commissions. This platform is really built for luxury wardrobes.

~~~~light,${colors.white}
#### WhatNot: The Live-Selling Experience

WhatNot puts a twist on traditional selling with livestream auctions and community-based commerce. It's part marketplace, part entertainment platform.

**Best for**: Sellers who enjoy live interaction and want to build a following

**Effort level**: Medium to high—requires hosting or managing live shows

**Earnings**: 
- Platform fee: 8% of sale price
- Payment processing: 2.9% + $0.30
- Top sellers earn $5,000-30,000+ monthly
- Average show generates $200-1,500 in sales

**Show Duration**: Typically 1-4 hours per show

**Audience Size**: Successful sellers build followings of 1,000-50,000+ followers

**Shipping**: You handle shipping after each show (typically $5-15 per item)

**Buyer audience**: Enthusiastic, community-driven shoppers

**Seller profile**: Influencer-types, vintage sellers, or resellers with personality

**Success Factors**: 
- Regular show schedule (3-7 shows per week)
- Unique personality and engagement
- Quality inventory and presentation
- Social media promotion

If you like the idea of selling clothes on camera and building a fan base, WhatNot is a unique and powerful platform to explore.

~~~~light,${colors.white}
#### Poshmark: Social Meets Selling

Poshmark combines social networking with secondhand selling. You create a "closet," upload photos, share listings, and engage with the community.

**Best for**: Fashion-focused sellers in the U.S. or Canada

**Effort level**: Moderate—listing is easy, but success requires active sharing

**Earnings**: 
- Sales over $15: You keep 80% (20% fee to Poshmark)
- Sales under $15: Flat $2.95 fee (you keep the rest)
- Average seller earns $100-1,500 monthly
- Top sellers earn $5,000+ monthly

**Shipping**: Buyer pays $8.27 for items under 5 lbs, $23.19 for items 5-10 lbs

**Listing Process**: Free to list, no upfront fees

**Buyer audience**: Style-conscious shoppers, primarily women (85% of users)

**Seller profile**: Part-time resellers, fashion lovers, curated closet sellers

**Success Metrics**: 
- Items typically sell within 30-60 days
- Sharing 3-5 times daily increases visibility by 200-300%
- Bundle discounts increase average order value by 40%

Poshmark's friendly, active community makes it great for selling name brands and trend-driven fashion.

~~~~light,${colors.white}
#### Mercari: Fast and Flexible

Mercari is a simple, no-frills resale app that makes it easy to sell just about anything, including clothes. It's popular for quick flips and fast fashion.

**Best for**: Sellers looking to move everyday items quickly

**Effort level**: Low to moderate—easy listing and shipping tools

**Earnings**: 
- Platform fee: 10% of sale price
- Payment processing: 2.9% + $0.30
- You keep approximately 87% of sale price
- Average item sells for $15-50

**Shipping Options**: 
- Mercari shipping: $4.99-12.99 depending on weight
- Free shipping: You pay shipping costs
- Local pickup: No shipping fees

**Listing Process**: Free to list, no upfront fees

**Buyer audience**: Bargain hunters and budget-conscious shoppers

**Seller profile**: Casual users, declutterers, and small resellers

**Success Metrics**: 
- Items typically sell within 7-30 days
- 90% of items sell within 90 days
- Lower-priced items ($10-30) sell fastest

Mercari is a strong choice if you want to get rid of items without too much fuss—and don't mind slightly lower payouts.

~~~~light,${colors.white}
#### Depop: Where Fashion Meets Aesthetic

If your items are trendy, vintage, or visually unique, Depop is your spot. With a social media-like interface, it's especially popular with Gen Z buyers who shop based on style and vibe.

**Best for**: Trendy, aesthetic, or vintage fashion

**Effort level**: Moderate—success requires great photos and tagging

**Earnings**: 
- Platform fee: 10% of sale price
- Payment processing: 2.9% + $0.30
- You keep approximately 87% of sale price
- Average item sells for $20-80

**Shipping**: 
- Depop shipping: $4.75-12.50 depending on weight
- Free shipping: You pay shipping costs
- International shipping available

**Listing Process**: Free to list, no upfront fees

**Buyer audience**: Young, style-savvy shoppers (primarily 16-35 age range)

**Seller profile**: Creatives, trendsetters, and niche curators

**Success Metrics**: 
- Items typically sell within 14-45 days
- Trending hashtags increase visibility by 150-200%
- Vintage items sell 30% faster than contemporary pieces

Depop is ideal if you love fashion, enjoy building a brand, and want to attract a visually-driven audience.

~~~~light,${colors.white}
#### So… Which Platform Should You Use?

| Platform | Effort | Best For | Max Earnings | Average Monthly | Processing Time |
|----------|--------|----------|--------------|-----------------|-----------------|
| ThredUp | Low | Hands-off bulk cleanouts | $50-300 | $100-500 | 2-4 weeks |
| eBay | High | Rare, collectible, or niche items | $2,000-10,000+ | $500-3,000 | 30-90 days |
| TheRealReal | Low | Authentic designer goods | $1,000-5,000 | $300-1,500 | 2-3 weeks |
| WhatNot | High | Live selling with personality | $5,000-30,000+ | $1,000-5,000 | Immediate |
| Poshmark | Medium | Name-brand fashion | $1,000-5,000+ | $200-1,500 | 30-60 days |
| Mercari | Low | Everyday items and quick flips | $500-2,000 | $100-800 | 7-30 days |
| Depop | Medium | Trendy, vintage, aesthetic items | $1,000-3,000 | $200-1,000 | 14-45 days |

~~~~light,${colors.white}
#### Final Thoughts

The best place to sell clothes online depends on your goals:

**Want it done for you?** ThredUp or The RealReal are your go-to.

**Want control and maximum profit?** eBay is king.

**Love being on camera and engaging live?** Try WhatNot.

**Selling cute or curated fashion?** Poshmark or Depop shine.

**Want fast, simple sales?** Mercari is quick and easy.

No matter which you choose, the secondhand market is thriving. The key is matching your selling style, time availability, and inventory type to the platform that best serves your needs.
`;

export default sellClothesOnline;
